// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CheckCircle,
  ExpandMoreRounded,
  WarningAmberRounded,
} from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { keyframes } from '@emotion/react';
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Decimal } from 'decimal.js';
import { useEffect, useState } from 'react';

interface Props {
  billingPlanTotal: Decimal;
  billingPlanChargeCount: number;
  billingPlanInvoiceDateCount: number;
  chargeListTotal: Decimal;
  chargeListChargeCount: number;
  chargeListInvoiceDateCount: number;
  hasGeneratedCharges: boolean;
  isConfigOutOfSync: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  currencyCode: IsoCurrencyCode;
}

const bumpAttention = keyframes`
  0% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-12px);
  }
  100% {
    transform: translateY(0);
  }
`;
const WARNING_STRIP_BG = 'rgba(237, 108, 2, 0.08)';
const SUCCESS_STRIP_BG = 'rgba(46, 125, 50, 0.06)';

export function BillingPlanReadinessSection(props: Props) {
  const rows = [
    {
      label: 'Total',
      billingPlanValue: formatCurrencyValue({
        amount: props.billingPlanTotal,
        isoCode: props.currencyCode,
      }),
      chargeListValue: formatCurrencyValue({
        amount: props.chargeListTotal,
        isoCode: props.currencyCode,
      }),
      isMatch: props.billingPlanTotal.equals(props.chargeListTotal),
    },
    {
      label: 'Charges',
      billingPlanValue: String(props.billingPlanChargeCount),
      chargeListValue: String(props.chargeListChargeCount),
      isMatch: props.billingPlanChargeCount === props.chargeListChargeCount,
    },
    {
      label: 'Invoice dates',
      billingPlanValue: String(props.billingPlanInvoiceDateCount),
      chargeListValue: String(props.chargeListInvoiceDateCount),
      isMatch:
        props.billingPlanInvoiceDateCount === props.chargeListInvoiceDateCount,
    },
  ];
  const hasMismatches = rows.some((row) => !row.isMatch);
  const showConfigWarning =
    props.hasGeneratedCharges && (props.isConfigOutOfSync || hasMismatches);
  const [isBumping, setIsBumping] = useState(false);

  useEffect(() => {
    if (!showConfigWarning) {
      setIsBumping(false);
      return;
    }

    setIsBumping(true);
    const timeoutId = window.setTimeout(() => {
      setIsBumping(false);
    }, 450);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [showConfigWarning]);

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        boxShadow: showConfigWarning ? 1 : 'none',
        animation: isBumping ? `${bumpAttention} 450ms ease-out` : 'none',
        transition: 'box-shadow 150ms ease',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 0.75,
          minHeight: 48,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: showConfigWarning
            ? WARNING_STRIP_BG
            : props.hasGeneratedCharges
              ? SUCCESS_STRIP_BG
              : 'transparent',
          transition: 'background-color 150ms ease',
        }}
      >
        <Typography variant="subtitle2" sx={{ minWidth: 0 }}>
          Billing plan cross-check
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {props.hasGeneratedCharges ? (
            showConfigWarning ? (
              <>
                <WarningAmberRounded
                  fontSize="small"
                  sx={{ color: 'warning.main' }}
                />
                <Typography
                  variant="body2"
                  color="warning.dark"
                  sx={{ lineHeight: 1 }}
                >
                  Please review
                </Typography>
              </>
            ) : (
              <>
                <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />
                <Typography variant="body2" color="success.dark">
                  In sync
                </Typography>
              </>
            )
          ) : (
            <Typography variant="body2" color="text.secondary">
              Charges not yet generated
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          aria-label={
            props.isExpanded
              ? 'Collapse billing plan cross-check'
              : 'Expand billing plan cross-check'
          }
          onClick={props.onToggle}
          sx={{ mr: -0.5 }}
        >
          <ExpandMoreRounded
            fontSize="small"
            sx={{
              transform: props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms ease',
            }}
          />
        </IconButton>
      </Box>

      <Collapse in={props.isExpanded} unmountOnExit>
        <Box
          sx={{
            px: 3,
            pb: 1.5,
            pt: 1.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          {showConfigWarning && (
            <Alert
              severity="warning"
              icon={<WarningAmberRounded fontSize="small" />}
              sx={{ mb: 1.5, py: 0.5 }}
            >
              <Typography variant="body2">
                Billing configuration has changed. You may want to regenerate
                the charge list.
              </Typography>
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto auto auto',
              columnGap: 4,
              rowGap: 1,
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Metric
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ justifySelf: 'end' }}
            >
              Billing plan
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ justifySelf: 'end' }}
            >
              Charge list
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ justifySelf: 'end' }}
            >
              Status
            </Typography>

            {rows.map((row) => (
              <Box
                key={row.label}
                sx={{
                  display: 'contents',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    pt: 1,
                    pb: 0.375,
                    borderTop: 1,
                    borderTopStyle: 'dotted',
                    borderTopColor: 'divider',
                  }}
                >
                  {row.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    justifySelf: 'end',
                    pt: 1,
                    pb: 0.375,
                    borderTop: 1,
                    borderTopStyle: 'dotted',
                    borderTopColor: 'divider',
                  }}
                >
                  {row.billingPlanValue}
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    props.hasGeneratedCharges ? undefined : 'text.secondary'
                  }
                  sx={{
                    justifySelf: 'end',
                    pt: 1,
                    pb: 0.375,
                    borderTop: 1,
                    borderTopStyle: 'dotted',
                    borderTopColor: 'divider',
                  }}
                >
                  {props.hasGeneratedCharges ? row.chargeListValue : 'Not set'}
                </Typography>
                <Box
                  sx={{
                    justifySelf: 'center',
                    display: 'flex',
                    pt: 1,
                    pb: 0.375,
                    borderTop: 1,
                    borderTopStyle: 'dotted',
                    borderTopColor: 'divider',
                  }}
                >
                  {props.hasGeneratedCharges ? (
                    row.isMatch ? (
                      <CheckCircle
                        fontSize="small"
                        aria-label={`${row.label} matches`}
                        sx={{ color: 'success.main' }}
                      />
                    ) : (
                      <Tooltip
                        arrow
                        title="Inconsistency between billing plan and charge list: this is allowed but may indicate an issue"
                      >
                        <WarningAmberRounded
                          fontSize="small"
                          aria-label={`${row.label} mismatch`}
                          sx={{ color: 'warning.main' }}
                        />
                      </Tooltip>
                    )
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Not set
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
